import React from 'react';
import './ProgressBar.scss';

/**
 * Default function to determine progress class based on percentage
 * 
 * @param {number} value - The progress percentage value
 * @returns {string} CSS class name for the progress bar color
 */
const defaultGetProgressClass = (value) => {
    if (value >= 80) return 'progress-fill--high';      
    if (value >= 50) return 'progress-fill--medium'; 
    if (value < 50) return 'progress-fill--low';  
    return 'progress-fill--low';                         
};

/**
 * Helper function to determine color level based on percentage
 * Used for custom colors mapping
 * 
 * @param {number} percentage - The progress percentage
 * @returns {string} Color level ('high', 'medium', 'low')
 */
const getColorLevel = (percentage) => {
    if (percentage >= 80) return 'high';
    if (percentage >= 50) return 'medium';
    return 'low';
};


/**
 * ProgressBar Component
 * 
 * A highly configurable and robust progress bar component with color-coded progress levels
 * 
 * @param {object} props - Component props
 * @param {number} props.percentage - The progress percentage (0-100)
 * @param {string} [props.size='medium'] - Size variant ('small', 'medium', 'large')
 * @param {boolean} [props.showPercentage=true] - Whether to show percentage text
 * @param {string} [props.statusText=''] - Custom status text to show after percentage (e.g., '14/28')
 * @param {string} [props.className=''] - Additional CSS classes
 * @param {function} [props.getProgressClass] - Custom function to determine progress class
 * @param {object} [props.customColors] - Custom colors object { high: '#color', medium: '#color', low: '#color' }
 * @param {string} [props.customProgressClass=''] - Custom CSS class to override default progress styling
 * @param {boolean} [props.showCustomTextOnly=false] - Show only custom status text (no percentage)
 * @returns {JSX.Element} Rendered progress bar component
 */
export const ProgressBar = ({ 
    percentage = 0, 
    size = 'medium', 
    showPercentage = true, 
    statusText = '',
    showCustomTextOnly = false,
    containerStyle = {},
}) => {
    const normalizedPercentage = Math.max(0, Math.min(100, percentage));
    const progressClass = defaultGetProgressClass(normalizedPercentage);
    
    // Build container custom styles
    const containerCustomStyle = { ...containerStyle };
    
    // Build display text
    let displayText = '';
    if (showCustomTextOnly && statusText) {
        displayText = statusText;
    } else if (showPercentage && statusText) {
        displayText = `${normalizedPercentage}% (${statusText})`;
    } else if (showPercentage) {
        displayText = `${normalizedPercentage}%`;
    } else if (statusText) {
        displayText = statusText;
    }
    
    return (
        <div className={`ProgressBar ProgressBar--${size}`} style={containerCustomStyle}>
            <div 
                className="ProgressBar__container"
            >
                <div 
                    className={`ProgressBar__fill ${progressClass}`}
                    style={{ width: `${normalizedPercentage}%` }}
                />
            </div>
            {(showPercentage || statusText) && (
                <span className="ProgressBar__text">{displayText}</span>
            )}
        </div>
    );
};